/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import { Typography as Text } from '@mui/material';

import Button from '@mui/material/Button';

import LinearProgress from '@mui/material/LinearProgress';
import CheckIcon from '@mui/icons-material/Check';
import CheckIconFilled from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

function getErrorMessage(error, t) {
  switch (error.code) {
    case 'lang_required':
      return t('Språk saknas');
    case 'lang_unsupported':
      return t('Språk stöds inte');
    case 'output_format_invalid':
      return t('Ogiltigt formaterings format');
    case 'forbidden':
      return t('Du har inte tillåtelse att ladda upp filer');
    case 'org_invalid_language':
      return t('Din arbetsyta har inte stöd för det valda språket');
    case 'invalid_url':
      return t('Ogiltig URL');
    case 'invalid_source':
      return t('Ogiltig uppladdningskälla');
    case 'name_missing':
      return t('Namn på fil saknas');
    case 'rate_limit':
      if (error.org_type !== 'enterprise') {
        return t(
          'Du har nått din gräns på {{limit}} filer denna senaste timmen. Uppgradera för att kunna ladda upp fler filer varje timme',
          { limit: error.limit }
        );
      }
      return t(
        'Du har nått din gräns på {{limit}} filer denna senaste timmen. Kontakta support för mer information',
        { limit: error.limit }
      );
    case 'invalid_youtube_id':
      return t('Ogiltigt YouTube länk');
    case 'phone_forbidden':
      return t('Du har inte tillgång att använda telefonnummer');
    case 'no_phone_credits':
      return t('Du har inte tillräckligt med krediter för att ringa');
    default:
      return error.msg || error;
  }
}

export default function UploadProgress({
  fileName,
  fileProgress,
  progress,
  files,
  uploadDone,
  uploadError,
  onClose,
  fileSize,
}) {
  const { t } = useTranslation();

  const errorMessage = uploadError && getErrorMessage(uploadError, t);

  const multi = files?.length > 1;

  if (uploadDone) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          css={css`
            padding: 8px;
            border-radius: 50%;
            background-color: var(--success-12p);
            display: inline-block;
            height: 51px;
            width: 51px;

            display: flex;
            align-items: center;
            justify-content: center;

            box-sizing: border-box;

            margin-bottom: 1rem;
          `}
        >
          <CheckIconFilled color="success" fontSize="large" />
        </div>
        <Text variant="subtitle1" fontWeight="700">
          {multi
            ? t('Dina filer är nu uppladdade')
            : t('Din fil är nu uppladdad')}
        </Text>
        {multi && (
          <div
            css={css`
              width: 70%;

              margin-top: 0.5rem;
              margin-bottom: 0.5rem;
            `}
          >
            {files.map((file, index) => (
              <div
                key={index}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  gap: 0.5rem;
                `}
              >
                <CheckIcon color="success" />
                <Text key={index} variant="subtitle2" fontWeight="700">
                  {file.name}
                </Text>
              </div>
            ))}
          </div>
        )}
        <Text variant="subtitle2" color="text.secondary" fontWeight="700">
          {multi
            ? t('Dina filer kommer att behandlas')
            : t('Nu kommer filen att behandlas')}
        </Text>
        <div
          css={css`
            width: 100%;
            display: flex;
            margin-top: 1rem;
          `}
        >
          <Button variant="outlined" onClick={onClose} fullWidth>
            {t('Stäng Fönster')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      {uploadError && (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0.5rem;

            border: 1px solid var(--error-50p);
            border-radius: 0.5rem;
            background-color: var(--error-4p);
            width: 100%;
            align-items: center;
            justify-content: center;
            margin-bottom: 1rem;
          `}
        >
          <CancelIcon color="error" />
          <Text
            variant="body2"
            css={css`
              margin-left: 0.5rem;
            `}
          >
            {errorMessage}
          </Text>
        </div>
      )}
      <div
        css={css`
          width: 100%;

          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          background-color: var(--primary-8p);
          border-radius: 0.5rem;
          padding: 1rem;
          box-sizing: border-box;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.875rem;
          `}
        >
          {uploadDone || progress === 100 ? (
            <CheckIconFilled
              color="inherit"
              css={css`
                color: white;
                background-color: var(--success);
              `}
            />
          ) : null}
          <Text variant="subtitle2" fontWeight="700">
            {fileName}
          </Text>
        </div>
        {(!uploadDone || progress < 100) && (
          <React.Fragment>
            <LinearProgress
              variant="determinate"
              value={uploadDone ? 100 : progress}
            />
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 0.5rem;
              `}
            >
              <Text variant="subtitle2" color="text.secondary" fontWeight="700">
                {t('Uppladdning')}
              </Text>
              <Text variant="subtitle2" color="text.secondary" fontWeight="700">
                {fileSize}
              </Text>
            </div>
          </React.Fragment>
        )}
      </div>
      {(uploadDone || uploadError) && (
        <div
          css={css`
            width: 100%;
            display: flex;
            margin-top: 1.5rem;
          `}
        >
          <Button variant="outlined" onClick={onClose} fullWidth>
            {t('Stäng Fönster')}
          </Button>
        </div>
      )}
    </div>
  );
}
